import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { SERVICES, findCatalogEntry } from "@/constants/cosecCatalog";

export default function ServicePage({ entry }) {
  const related = (entry.related || []).map((s) => findCatalogEntry(s)).filter(Boolean);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        <div className="container-cv relative pt-14 md:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> {entry.eyebrow}</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-primary leading-[1.04]">
              {entry.title}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-accent">{entry.highlight}</span>
                <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
              </span>
            </h1>
            <p className="mt-5 font-display text-lg md:text-xl font-semibold text-primary/80 leading-snug max-w-2xl">{entry.subhead}</p>
            <p className="mt-5 text-base md:text-[17px] text-muted-foreground leading-relaxed max-w-2xl">{entry.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild data-testid="service-cta-contact" className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                <Link to="/contact">Talk to sales <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild data-testid="service-cta-pricing" variant="outline" className="h-12 px-6 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-accent/20 via-transparent to-primary/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl border border-border bg-white p-5 overflow-hidden">
                <img src={entry.image} alt={entry.title} className="rounded-2xl h-72 w-full object-cover" />
                {entry.pricing && (
                  <div className="mt-5 rounded-xl border border-accent/40 bg-accent/10 p-4 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-primary/60">From</div>
                      <div className="font-display text-3xl font-bold text-primary leading-none">{entry.pricing.from}</div>
                      <div className="text-xs text-muted-foreground mt-1">{entry.pricing.plan}</div>
                    </div>
                    <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                      <Link to="/contact">Get started</Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {entry.benefits?.length > 0 && (
        <section className="border-y border-border bg-secondary/40">
          <div className="container-cv py-10 md:py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {entry.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="lime-tick mt-0.5"><CheckCircle2 className="h-4 w-4" /></span>
                <div>
                  <div className="font-display font-bold text-primary text-sm">{b.t}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{b.d}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* What's included + How it works */}
      <section className="py-20 md:py-24">
        <div className="container-cv grid lg:grid-cols-12 gap-12">
          {entry.includes?.length > 0 && (
            <div className="lg:col-span-6">
              <span className="section-eyebrow">What&apos;s included</span>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
                Everything you need in one fixed price.
              </h2>
              <ul className="mt-6 space-y-3">
                {entry.includes.map((inc, i) => (
                  <li key={i} className="flex items-start gap-3 text-primary">
                    <span className="lime-tick mt-0.5"><CheckCircle2 className="h-4 w-4" /></span>
                    <span className="text-base">{inc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {entry.howItWorks?.length > 0 && (
            <div className="lg:col-span-6">
              <span className="section-eyebrow">How it works</span>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">A simple flow, every time.</h2>
              <ol className="mt-6 space-y-5">
                {entry.howItWorks.map((step, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-accent text-primary font-display font-bold text-sm shrink-0">{i + 1}</span>
                    <div>
                      <div className="font-display font-bold text-primary">{step.t}</div>
                      <div className="text-sm text-muted-foreground mt-1 max-w-md leading-relaxed">{step.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </section>

      {/* FAQs */}
      {entry.faqs?.length > 0 && (
        <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
          <div className="container-cv max-w-4xl">
            <div className="text-center">
              <span className="section-eyebrow">FAQs</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">Common questions</h2>
            </div>
            <div className="mt-10">
              <Accordion type="single" collapsible className="space-y-3">
                {entry.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} data-testid={`svc-faq-${i + 1}`} className="rounded-xl border border-border bg-white px-5">
                    <AccordionTrigger className="text-left font-display font-bold text-primary hover:no-underline py-5">
                      <span className="flex items-baseline gap-3">
                        <span className="font-mono text-xs font-bold text-accent">{String(i + 1).padStart(2, "0")}</span>
                        <span>{f.q}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="container-cv">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">Related services</h2>
              <Link to="/services" className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent">All services <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/${r.slug}`}
                  className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-accent group-hover:text-primary transition-colors">
                      <r.Icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-primary tracking-tight">{r.title.replace(/[-,.]$/, "")} <span className="text-accent">{r.highlight}</span></h3>
                  <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{r.subhead}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              Stop chasing statutory deadlines.{" "}
              <span className="text-accent">Hand it over.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Book a Demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

// Convenience export, useful in the catalog router
export { SERVICES };
